import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useFormValidation } from '@/hooks/use-form-validation';
import { useLogger } from '@/utils/logging/logger';

type UseRegisterStateReturn = {
  // Auth state
  isAuthenticated: boolean;
  
  // Form state
  form: Record<string, { 
    value: string; 
    error: string; 
    touched: boolean; 
    rules: { 
      required?: boolean; 
      minLength?: number; 
      maxLength?: number; 
      pattern?: RegExp; 
      passwordMatch?: string; 
    } 
  }>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  validateForm: () => boolean;
  
  // Local state
  isLoading: boolean;
  termsAccepted: boolean;
  activeTab: string;
  error: string | null;
  
  // Handlers
  setTermsAccepted: (accepted: boolean) => void;
  setActiveTab: (tab: string) => void;
  setError: (error: string | null) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
};

export const useRegisterState = (): UseRegisterStateReturn => {
  const logger = useLogger('useRegisterState');
  const navigate = useNavigate();
  const { signUp, isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [activeTab, setActiveTab] = useState("email");
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);
  
  const { form, handleChange, validateForm } = useFormValidation(
    {
      fullName: '',
      email: '',
      password: '',
      confirmPassword: ''
    },
    {
      fullName: { required: true, minLength: 2 },
      email: { 
        required: true, 
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ 
      },
      password: { 
        required: true, 
        minLength: 8, 
        pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/ 
      },
      confirmPassword: { 
        required: true, 
        passwordMatch: 'password'
      }
    }
  );
  
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    
    if (!validateForm()) return;
    
    if (!termsAccepted) {
      setError("Пожалуйста, примите условия пользования");
      return;
    }
    
    setIsLoading(true);
    try {
      const userData = {
        fullName: form.fullName.value
      };
      
      await signUp(form.email.value, form.password.value, userData);
    } catch (error: unknown) {
      logger.error('Registration error', error);
      const message = (error as { message?: string } | null)?.message ?? 'Произошла ошибка при регистрации';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    // Auth state
    isAuthenticated,
    
    // Form state
    form,
    handleChange,
    validateForm,
    
    // Local state
    isLoading,
    termsAccepted,
    activeTab,
    error,
    
    // Handlers
    setTermsAccepted,
    setActiveTab,
    setError,
    handleSubmit
  };
};
